import {
  Button,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Select,
  Spin,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { ROLES, User } from '../../../../types';
import { useEffect, useState } from 'react';
import { createUser, updateUser } from '../../../../utils/httpRequest';
import { roles } from '../../../../constants';
import { useSelector } from 'react-redux';
import { selectCompanies } from '../../../../slices/companies';

export interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isEdit?: boolean;
  user?: User;
}

const CreateUserModal = ({
  open,
  onClose,
  onConfirm,
  isEdit,
  user,
}: CreateUserModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [roleId, setRoleId] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const companies = useSelector(selectCompanies);

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const formValues = form.getFieldsValue();
      const data = {
        ...user,
        ...form.getFieldsValue(),
        roles: [
          {
            name: formValues.role as ROLES,
          },
        ],
      };
      await (isEdit ? updateUser(data) : createUser(data));
      onConfirm();
    } catch (err: any) {
      if (!err?.errorFields) {
        messageApi.error(err?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    form.resetFields();
    if (user) {
      form.setFieldsValue(user);
      setRoleId(user.roles?.[0]?.name);
    }
  }, [user]);

  return (
    <Modal
      title={isEdit ? `Edit User "${user?.username}"` : 'Add New User'}
      open={open}
      closable
      onCancel={onClose}
      footer={
        <Flex justify="flex-end" gap={16}>
          <Button type="primary" ghost onClick={onClose} loading={loading}>
            {t('cancel')}
          </Button>
          <Button type="primary" onClick={handleConfirm} loading={loading}>
            {t('confirm')}
          </Button>
        </Flex>
      }
    >
      {contextHolder}
      <Typography.Text type="secondary">
        Please fill user information to continue.
      </Typography.Text>
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" style={{ margin: 20 }}>
          <Form.Item
            label="Full Name"
            name="username"
            rules={[
              {
                required: true,
                message: 'Please input user name!',
              },
            ]}
          >
            <Input type="text" placeholder="Enter" />
          </Form.Item>
          <Form.Item
            label="Role"
            name="role"
            rules={[
              {
                required: true,
                message: 'Please select role!',
              },
            ]}
          >
            <Select
              options={roles}
              placeholder="Select"
              onChange={(value) => setRoleId(value)}
            />
          </Form.Item>
          {roleId !== ROLES.ROLE_SUPERADMIN && (
            <Form.Item
              label="Company"
              name="companyID"
              rules={[
                {
                  required: true,
                  message: 'Please select Company!',
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={companies.map((i) => ({
                  label: i.name,
                  value: i.companyID,
                }))}
                placeholder="Select Company"
              />
            </Form.Item>
          )}
          <Form.Item
            label="Email"
            name="email"
            rules={[
              {
                required: true,
                message: 'Please input email!',
              },
            ]}
          >
            <Input type="text" placeholder="Enter" />
          </Form.Item>
          <Form.Item
            label={isEdit ? 'Password' : 'Initial Password'}
            name="password"
            rules={
              isEdit
                ? []
                : [
                    {
                      required: true,
                      message: 'Please input password!',
                    },
                  ]
            }
          >
            <Input.Password
              placeholder="Enter"
              visibilityToggle={{
                visible: passwordVisible,
                onVisibleChange: setPasswordVisible,
              }}
            />
          </Form.Item>
          <Form.Item
            label="Status"
            name="isActive"
            rules={[
              {
                required: true,
                message: 'Please select Status!',
              },
            ]}
          >
            <Select
              options={[
                {
                  label: 'Active',
                  value: true,
                },
                { label: 'Deactivated', value: false },
              ]}
              placeholder="Select"
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateUserModal;
